import { all, call, put, select, takeLatest } from "redux-saga/effects";
import * as Sentry from "@sentry/browser";
import { billingAPI } from "../api/api";
import { actionTypes } from "../store/reducers/billingActionTypes";
import { billingActions } from "../store/actions/creators/billingActions";
import { generalActions } from "../store/reducers/generalReducer";
import i18n from "../../res/languages";
import { actionTypes as authActionTypes } from "../store/reducers/authReducer";
import { CancelSubscriptionPayload } from "../../types/billing";
import { clearWelcomeSplashDismissedLocally } from "../../utils/welcomeSplashDismissal";

function* getPlans(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getPlans);
    if (response.status === 200) {
      yield put(billingActions.setPlans(response.data.data));
    }
  } catch (error: any) {
    yield put(billingActions.setError(error.response?.data?.message || "Failed to load plans"));
  }
}

function* getSubscription(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getSubscription);
    if (response.status === 200) {
      yield put(billingActions.setSubscription(response.data));
    }
  } catch (error: any) {
    yield put(billingActions.setError(error.response?.data?.message || "Failed to load subscription"));
  }
}

function* createCheckoutSession(action: any): Generator<any, void, any> {
  try {
    const baseUrl = window.location.origin;
    const response: any = yield call(billingAPI.createCheckoutSession, {
      success_url: `${baseUrl}/billing?success=true`,
      cancel_url: `${baseUrl}/billing?canceled=true`,
      ...action.data,
    });

    if (response.status === 200) {
      yield put({ type: actionTypes.CREATE_CHECKOUT_SESSION_SUCCESS });
      window.location.href = response.data.checkout_url;
    }
  } catch (error: any) {
    yield put(billingActions.setError(error.response?.data?.message || "Checkout failed"));
    yield put(generalActions.errorAC({ message: "Checkout failed", type: "error" }));
  }
}

function* createPortalSession(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.createPortalSession);
    if (response.status === 200) {
      yield put({ type: actionTypes.CREATE_PORTAL_SESSION_SUCCESS });
      window.location.href = response.data.portal_url;
    }
  } catch (error: any) {
    yield put(billingActions.setError(error.response?.data?.message || "Portal failed"));
    yield put(generalActions.errorAC({ message: "Portal failed", type: "error" }));
  }
}

function* getInvoices(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getInvoices, action.params);
    if (response.status === 200) {
      yield put(billingActions.setInvoices(response.data.data));
    }
  } catch (error: any) {
    yield put(billingActions.setError(error.response?.data?.message || "Failed to load invoices"));
  }
}

// Reads a Laravel error response into what the account dialogs show inline:
// the first message per field (422 `errors`), the overall message, and the machine `code`.
const readAccountError = (error: any, fallbackKey: string) => {
  const data = error?.response?.data ?? {};
  const fieldErrors: Record<string, string> = {};
  Object.entries(data.errors ?? {}).forEach(([field, messages]) => {
    const first = Array.isArray(messages) ? messages[0] : messages;
    if (typeof first === "string") fieldErrors[field] = first;
  });
  return {
    message: (data.message as string | undefined) || i18n.t(fallbackKey),
    fieldErrors,
    code: (data.code as string | undefined) ?? null,
  };
};

// After every account action refetch what the Billing page and the rest of the app read:
// the subscription payload (account_state / closes_at / delete_after) and /me (billing.*).
function* refreshAfterAccountAction(): Generator<any, void, any> {
  yield call(getSubscription);
  // Same action the getUser() creator builds (importing the creator drags the whole request
  // layer into every billing saga test).
  yield put({ type: authActionTypes.GET_USER });
}

export function* cancelSubscription(action?: { type?: string; payload?: CancelSubscriptionPayload }): Generator<any, void, any> {
  const { afterEnd, confirmName, inline } = action?.payload ?? {};
  try {
    const body = afterEnd
      ? { after_end: afterEnd, ...(afterEnd === "close" ? { confirm_name: confirmName } : {}) }
      : undefined;
    const response: any = yield call(billingAPI.cancelSubscription, body);
    if (response.status === 200) {
      yield put(generalActions.successAC({ message: response.data.message, type: "success" }));
      yield put(billingActions.accountActionSuccess());
      yield call(refreshAfterAccountAction);
    }
  } catch (error: any) {
    // Surface the server's real explanation instead of a generic string — most
    // importantly the SUBSCRIPTION_NOT_PURCHASED refusal for a donation/free-sourced
    // plan ("This plan comes from a monthly donation, so it cannot be canceled here.
    // Manage the donation itself to stop it."), which was previously thrown away here.
    const parsed = readAccountError(error, "billing.cancellation_failed");
    yield put(billingActions.setError(parsed.message));
    yield put(billingActions.accountActionError(parsed));
    if (!inline) {
      yield put(generalActions.errorAC({ message: parsed.message, type: "error" }));
    }
  }
}

// Close an account that has no plan to cancel (paused / view-only).
export function* closeAccount(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.closeAccount, action.payload.confirmName);
    if (response.status === 200) {
      yield put(generalActions.successAC({ message: response.data.message, type: "success" }));
      yield put(billingActions.accountActionSuccess());
      yield call(refreshAfterAccountAction);
    }
  } catch (error: any) {
    // The dialog shows this inline (422 confirm_name / ACTIVE_PLAN / ACTIVE_DONATION_PLAN, 403).
    const parsed = readAccountError(error, "billing.acct_cl_error");
    yield put(billingActions.setError(parsed.message));
    yield put(billingActions.accountActionError(parsed));
  }
}

// "Keep my account": withdraw a pending closure; the account becomes paused.
export function* keepAccount(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.keepAccount);
    if (response.status === 200) {
      yield put(generalActions.successAC({ message: response.data.message || i18n.t("billing.subscription_resumed"), type: "success" }));
      yield put(billingActions.accountActionSuccess());
      yield call(refreshAfterAccountAction);
    }
  } catch (error: any) {
    const parsed = readAccountError(error, "billing.acct_action_failed");
    yield put(billingActions.setError(parsed.message));
    yield put(billingActions.accountActionError(parsed));
    yield put(generalActions.errorAC({ message: parsed.message, type: "error" }));
  }
}

export function* resumeSubscription(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.resumeSubscription);
    if (response.status === 200) {
      yield put(generalActions.successAC({ message: response.data.message || i18n.t("billing.subscription_resumed"), type: "success" }));
      yield call(refreshAfterAccountAction);
    }
  } catch (error: any) {
    // Same fix as cancelSubscription above — a donation/free-sourced row refuses
    // resume with the same SUBSCRIPTION_NOT_PURCHASED code and a real explanation.
    const serverMessage = error.response?.data?.message;
    yield put(billingActions.setError(serverMessage || i18n.t("billing.resume_failed")));
    yield put(generalActions.errorAC({ message: serverMessage || i18n.t("billing.resume_failed"), type: "error" }));
  }
}

// Consented cancel of the account's own monthly donation. Distinct from
// cancelSubscription above: this is the ONLY endpoint allowed to touch a donation's
// Stripe subscription (see backend D8). The button that dispatches this is only ever
// shown for a donation-backed plan or an overlap, both cases where can_cancel/is_active
// tell the truth about whether pressing it does anything.
export function* cancelDonation(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.cancelDonation);
    if (response.status === 200) {
      yield put(billingActions.cancelDonationSuccess({ donation: response.data.donation }));
      // response.data.message already distinguishes a fresh request ("will stop at
      // the end of the period...") from a repeat press ("already been stopped") —
      // pass it straight through rather than re-deciding which copy applies.
      yield put(generalActions.successAC({ message: response.data.message, type: "success" }));
    }
  } catch (error: any) {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const serverMessage = error.response?.data?.message;
    // A Stripe-side failure (502 DONATION_CANCEL_FAILED) carries no useful server message
    // to show a donor — say what actually happened and that it's safe to retry. A 429 from
    // the endpoint's rate limiter (5/hour) gets Laravel's generic "Too Many Attempts." —
    // prefer our own copy over that. Every other case (422 NO_ACTIVE_DONATION, 403
    // NOT_YOUR_DONATION) already has a real, donor-facing server message worth showing as-is.
    let message = serverMessage;
    if (status === 502 && code === "DONATION_CANCEL_FAILED") {
      message = i18n.t("billing.donation_cancel_failed_retry");
    } else if (status === 429) {
      message = i18n.t("billing.donation_cancel_rate_limited");
    } else if (!message) {
      message = i18n.t("billing.donation_cancel_failed_generic");
    }
    yield put({ type: actionTypes.CANCEL_DONATION_ERROR });
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

// Payment Methods Sagas
function* getPaymentMethods(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getPaymentMethods);
    if (response.status === 200) {
      yield put(billingActions.setPaymentMethods(response.data.data || response.data));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.GET_PAYMENT_METHODS_ERROR, payload: error.response?.data?.message || "Failed to load payment methods" });
  }
}

function* addPaymentMethod(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.addPaymentMethod, action.paymentMethodId);
    if (response.status === 200 || response.status === 201) {
      yield put(billingActions.addPaymentMethodSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "Payment method added", type: "success" }));
      // Refresh payment methods to get updated list
      yield call(getPaymentMethods);
    }
  } catch (error: any) {
    yield put({ type: actionTypes.ADD_PAYMENT_METHOD_ERROR, payload: error.response?.data?.message || "Failed to add payment method" });
    yield put(generalActions.errorAC({ message: "Failed to add payment method", type: "error" }));
  }
}

function* deletePaymentMethod(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.deletePaymentMethod, action.id);
    if (response.status === 200 || response.status === 204) {
      yield put(billingActions.deletePaymentMethodSuccess(action.id));
      yield put(generalActions.successAC({ message: "Payment method deleted", type: "success" }));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.DELETE_PAYMENT_METHOD_ERROR, payload: error.response?.data?.message || "Failed to delete payment method" });
    yield put(generalActions.errorAC({ message: "Failed to delete payment method", type: "error" }));
  }
}

function* setDefaultPaymentMethod(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.setDefaultPaymentMethod, action.id);
    if (response.status === 200) {
      yield put(billingActions.setDefaultPaymentMethodSuccess(action.id));
      yield put(generalActions.successAC({ message: "Default payment method updated", type: "success" }));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.SET_DEFAULT_PAYMENT_METHOD_ERROR, payload: error.response?.data?.message || "Failed to set default payment method" });
    yield put(generalActions.errorAC({ message: "Failed to set default payment method", type: "error" }));
  }
}

function* getSetupIntent(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getSetupIntent);
    if (response.status === 200) {
      yield put(billingActions.setSetupIntentSecret(response.data.client_secret));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.GET_SETUP_INTENT_ERROR, payload: error.response?.data?.message || "Failed to create setup intent" });
  }
}

// Payment History Saga
function* getPaymentHistory(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getPaymentHistory, action.params);
    if (response.status === 200) {
      yield put(billingActions.setPaymentHistory(response.data.data || response.data));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.GET_PAYMENT_HISTORY_ERROR, payload: error.response?.data?.message || "Failed to load payment history" });
  }
}

// Customer Info Sagas
function* getCustomer(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getCustomer);
    if (response.status === 200) {
      yield put({ type: actionTypes.GET_CUSTOMER_SUCCESS, payload: response.data });
    }
  } catch (error: any) {
    yield put({ type: actionTypes.GET_CUSTOMER_ERROR, payload: error.response?.data?.message || "Failed to load customer info" });
  }
}

function* updateCustomer(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.updateCustomer, action.data);
    if (response.status === 200) {
      yield put({ type: actionTypes.UPDATE_CUSTOMER_SUCCESS, payload: { name: response.data.name, email: response.data.email } });
      yield put(generalActions.successAC({ message: "Customer information updated", type: "success" }));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.UPDATE_CUSTOMER_ERROR, payload: error.response?.data?.message || "Failed to update customer info" });
    yield put(generalActions.errorAC({ message: "Update failed", type: "error" }));
  }
}

function* getInvoice(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getInvoice, action.invoiceId);
    if (response.status === 200) {
      yield put({ type: actionTypes.GET_INVOICE_SUCCESS, payload: response.data });
    }
  } catch (error: any) {
    yield put({ type: actionTypes.GET_INVOICE_ERROR, payload: error.response?.data?.message || "Failed to load invoice" });
  }
}

export function* dismissWelcome(): Generator<any, void, any> {
  // The modal itself is already closed optimistically by WelcomeSplash's click
  // handler (and kept closed across a refresh via the local marker), so a
  // failure here has nothing left for the user to see or retry themselves —
  // authSaga's getUser retries this same PUT once on the next app load if it
  // never lands. But it must not go completely unnoticed: a real backend
  // regression here would otherwise only ever surface as vague "the trial
  // splash keeps coming back" reports with nothing to grep for.
  const workspaceId: any = yield select((state: any) => state.authStore?.workspace?.id);
  try {
    const response: any = yield call(() =>
      billingAPI.dismissWelcome()
    );
    if (response.status === 200 || response.status === 204) {
      yield put({ type: actionTypes.DISMISS_WELCOME_SUCCESS });
      clearWelcomeSplashDismissedLocally(workspaceId);
    }
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { area: "billing", action: "dismiss_welcome" },
      extra: { message: "Failed to persist welcome splash dismissal", workspaceId },
    });
  }
}

function* purchaseSetupFee(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.purchaseSetupFee, action.planId);
    if (response.status === 200 || response.status === 201) {
      yield put({ type: actionTypes.PURCHASE_SETUP_FEE_SUCCESS, payload: response.data });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    }
  } catch (error: any) {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;

    // 422 means the user already has a pending managed upload (paid but files not yet uploaded).
    // Show a targeted message directing them to Settings rather than a generic error.
    if (status === 422) {
      yield put(generalActions.errorAC({
        message: serverMessage || "You already have a pending managed upload. Please upload your files in Settings.",
        type: "error",
      }));
    } else {
      yield put(generalActions.errorAC({
        message: serverMessage || "Failed to purchase setup fee",
        type: "error",
      }));
    }

    // Clear the payment processing lock so the UI is not frozen
    yield put({ type: actionTypes.SET_PAYMENT_PROCESSING, payload: false });
  }
}

function* uploadManagedFile(action: any): Generator<any, void, any> {
  try {
    const formData = new FormData();
    const files: File[] = action.files;
    files.forEach((file) => {
      formData.append("files[]", file);
    });
    const response: any = yield call(billingAPI.uploadManagedFile, formData);
    if (response.status === 200 || response.status === 201) {
      yield put({ type: actionTypes.UPLOAD_MANAGED_FILE_SUCCESS });
      yield put(generalActions.successAC({ message: "Files uploaded successfully", type: "success" }));
      // Refresh full billing state from /me so managedUploadFileUploaded and status are up to date
      yield put({ type: "GET_USER" });
    }
  } catch (error: any) {
    yield put(generalActions.errorAC({
      message: error.response?.data?.message || "Failed to upload files",
      type: "error",
    }));
  }
}

function* getUsage(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.getUsage);
    if (response.status === 200) {
      yield put({ type: actionTypes.GET_USAGE_SUCCESS, payload: response.data });
    }
  } catch (error: any) {
    // Non-critical: silently ignore usage fetch errors
  }
}

function* confirmSetupPurchase(): Generator<any, void, any> {
  try {
    const response: any = yield call(billingAPI.confirmSetupPurchase);
    if (response.data.confirmed) {
      // Pass the full response data so the reducer can also reset managed upload state
      // when the backend has reset it for a repeat purchase.
      yield put({ type: actionTypes.CONFIRM_SETUP_PURCHASE_SUCCESS, payload: response.data });
      // Also refresh full billing state from /me so all fields (managedUploadRequested, etc.) are current
      yield put({ type: "GET_USER" });
    }
  } catch (error: any) {
    // Non-critical: silently ignore — Stripe confirmation is a best-effort fallback for webhook delay
  }
}

export function* billingWatcher() {
  yield all([
    takeLatest(actionTypes.GET_PLANS, getPlans),
    takeLatest(actionTypes.GET_SUBSCRIPTION, getSubscription),
    takeLatest(actionTypes.CREATE_CHECKOUT_SESSION, createCheckoutSession),
    takeLatest(actionTypes.CREATE_PORTAL_SESSION, createPortalSession),
    takeLatest(actionTypes.GET_INVOICES, getInvoices),
    takeLatest(actionTypes.CANCEL_SUBSCRIPTION, cancelSubscription),
    takeLatest(actionTypes.RESUME_SUBSCRIPTION, resumeSubscription),
    takeLatest(actionTypes.CLOSE_ACCOUNT, closeAccount),
    takeLatest(actionTypes.KEEP_ACCOUNT, keepAccount),
    takeLatest(actionTypes.CANCEL_DONATION, cancelDonation),
    // Payment Methods
    takeLatest(actionTypes.GET_PAYMENT_METHODS, getPaymentMethods),
    takeLatest(actionTypes.ADD_PAYMENT_METHOD, addPaymentMethod),
    takeLatest(actionTypes.DELETE_PAYMENT_METHOD, deletePaymentMethod),
    takeLatest(actionTypes.SET_DEFAULT_PAYMENT_METHOD, setDefaultPaymentMethod),
    takeLatest(actionTypes.GET_SETUP_INTENT, getSetupIntent),
    // Payment History
    takeLatest(actionTypes.GET_PAYMENT_HISTORY, getPaymentHistory),
    // Customer Info
    takeLatest(actionTypes.GET_CUSTOMER, getCustomer),
    takeLatest(actionTypes.UPDATE_CUSTOMER, updateCustomer),
    // Invoice Details
    takeLatest(actionTypes.GET_INVOICE, getInvoice),
    // Onboarding / managed upload
    takeLatest(actionTypes.DISMISS_WELCOME_TRIGGER, dismissWelcome),
    takeLatest(actionTypes.PURCHASE_SETUP_FEE_TRIGGER, purchaseSetupFee),
    takeLatest(actionTypes.UPLOAD_MANAGED_FILE_TRIGGER, uploadManagedFile),
    takeLatest(actionTypes.GET_USAGE_TRIGGER, getUsage),
    takeLatest(actionTypes.CONFIRM_SETUP_PURCHASE, confirmSetupPurchase),
  ]);
}
